import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { getChangeRequestBranchId } from '@gitbook/app-hive';
import { diffDocuments } from '@gitbook/doc-core';
import { type Revision, RevisionPageType, type RevisionTreePage } from '@gitbook/hive-core';
import { useHiveClient, useHiveWorkingDocumentById } from '@gitbook/hive-react';
import { Document, Value } from '@gitbook/slate';
import { TextSkeleton } from '@gitbook/spine-react';
import { getSuspense } from '@gitbook/util-suspense-cache';
import type { DiffTreeChange } from '@gitbook/util-tree-merge';

import { useContentKitRemote } from '../../../state';
import {
    BlockCommentButton,
    type BlockConfiguration,
    ContentEditor,
    useContentEditorIntegrationData,
} from '../..';

const blockConfiguration: BlockConfiguration = {
    withImages: true,
    withAPIs: true,
    withCompletions: false,
    withDrawings: true,
    withFiles: true,
    withReusableContent: true,
};

export type PageDocumentDiffProps = {
    organizationId: string;
    changeRequest: api.ChangeRequest;
    currentRevision: Revision;
    baseRevision: Revision;
    pageDiff: DiffTreeChange<RevisionTreePage>;
};

/**
 * Display the diff of a document for a given page diff.
 */
export function PageDocumentDiff(props: PageDocumentDiffProps) {
    const { pageDiff, baseRevision } = props;

    const hiveClient = useHiveClient();

    // Get the base document
    const basePage =
        pageDiff.type === 'modified'
            ? pageDiff.before.page
            : pageDiff.type === 'deleted'
              ? pageDiff.item.page
              : null;
    const baseDocumentId = basePage?.type === RevisionPageType.Document ? basePage.document : null;
    const baseDocument = baseDocumentId
        ? getSuspense(hiveClient.store.getDocument(baseRevision.contentId, baseDocumentId)) || null
        : null;

    if (pageDiff.type === 'deleted') {
        return (
            <PageDiffWithHeadDocument {...props} baseDocument={baseDocument} headDocument={null} />
        );
    }

    return <PageDiffWithHeadBranchDocument {...props} baseDocument={baseDocument} />;
}

function PageDiffWithHeadBranchDocument(
    props: PageDocumentDiffProps & {
        baseDocument: Document | null;
    }
) {
    const { currentRevision, changeRequest, pageDiff } = props;

    // Connect to the branch for the current document
    const branchDocument = useHiveWorkingDocumentById({
        branch: {
            contentId: currentRevision.contentId,
            branchId: getChangeRequestBranchId(changeRequest.id),
        },
        containerType: 'pages',
        containerId: pageDiff.item.page.id,
    });
    const headDocument = branchDocument.value.document;

    return <PageDiffWithHeadDocument {...props} headDocument={headDocument} />;
}

function PageDiffWithHeadDocument(
    props: PageDocumentDiffProps & {
        baseDocument: Document | null;
        headDocument: Document | null;
    }
) {
    const { baseDocument, headDocument, currentRevision, changeRequest, organizationId } = props;

    const diffValue = React.useMemo(() => {
        return Value.create({
            document: diffDocuments(
                baseDocument ?? Document.create(),
                headDocument ?? Document.create(),
                {
                    wrapUnmodifiedBlocks: true,
                }
            ),
        });
    }, [baseDocument, headDocument]);

    const integrationBlocksRemote = useContentKitRemote();

    const integrationBlocksContext = React.useMemo<api.ContentKitContextDocument>(
        () => ({
            type: 'document',
            editable: false,
            spaceId: currentRevision.contentId,
            theme: 'light',
        }),
        [currentRevision.contentId]
    );

    const integrationData = useContentEditorIntegrationData({
        spaceId: changeRequest.space,
        organizationId,
    });

    return (
        <div className="pr-8 pl-10">
            <ContentEditor
                readOnly
                value={diffValue}
                onChange={() => {}}
                integrationBlocksRemote={integrationBlocksRemote}
                integrationBlocksContext={integrationBlocksContext}
                integrationData={integrationData}
                blocks={blockConfiguration}
                selectedSection={null}
                rightGutterComponent={BlockCommentButton}
            />
        </div>
    );
}

/**
 * Skeleton for the PageDocumentDiff component.
 */
export function PageDocumentDiffSkeleton() {
    return (
        <div className="flex flex-col gap-2">
            <TextSkeleton className="w-full" />
            <TextSkeleton className="w-[75%]" />
            <TextSkeleton className="w-full" />
            <TextSkeleton className="w-[75%]" />
            <TextSkeleton className="w-[50%]" />
        </div>
    );
}
